/**
 * CeMM's colours, as the theme's colour scales.
 *
 * From CeMM's colour and font chart: Blue #00aecc, Blue 2 #1b88a7, Darkblue
 * #0f5568, Lightblue #bee3ef, Yellow #f1dd00, Green #96c03a, Purple #c73188,
 * Red #e95e30. There is no web style guide, so how they are used on screen is
 * decided here.
 *
 * Chakra's scales are re-tinted rather than a "brand" palette added beside
 * them: every page already says colorPalette="blue", "green", "orange", and
 * those names keep their meaning -- blue is action, green done, orange needs
 * attention -- while their colours become CeMM's. No page changes.
 *
 * Each chart colour sits at the step whose role it suits, and the steps
 * around it are built out from it, checked for contrast:
 *
 * - blue: Blue 2 is 600, which is what a solid button is (white on it 4.1:1
 *   -- under the 4.5:1 for small text, so if buttons read too light, Darkblue
 *   at 8.3:1 is the next try). CeMM Blue is 500, too light to carry white
 *   text (2.6:1), so it is the focus ring and highlights. Darkblue is 800,
 *   Lightblue 200. Links and coloured text use 700 (5.7:1 on white).
 * - green: CeMM Green is 500. It cannot carry white text (3.0:1), so a solid
 *   green -- Approve -- is 700 instead (4.7:1); see the semantic override.
 * - orange: CeMM Red is 500 -- it is an orange-red, and "needs attention" is
 *   what orange means here. Solid is 600 (4.6:1).
 * - purple: CeMM Purple is 500; programme and representative badges.
 * - yellow: CeMM Yellow is 500; black text on it, as Chakra already sets.
 * - gray: a cool grey leaning to Darkblue, so the neutral surfaces belong to
 *   the same family rather than to a generic grey.
 * - teal and cyan, still named in a few places, follow blue: they never
 *   meant anything blue did not.
 * - red stays Chakra's own: an error must not look like "needs attention".
 */

type Scale = Record<"50" | "100" | "200" | "300" | "400" | "500" | "600" | "700" | "800" | "900" | "950", { value: string }>;

function scale(values: [string, string, string, string, string, string, string, string, string, string, string]): Scale {
  const steps = ["50", "100", "200", "300", "400", "500", "600", "700", "800", "900", "950"] as const;
  return Object.fromEntries(steps.map((step, index) => [step, { value: values[index]! }])) as Scale;
}

const blue = scale([
  "#eef9fc", "#d7f0f7", "#bee3ef", "#8fd0e3", "#4fbcd6",
  "#00aecc", "#1b88a7", "#156f89", "#0f5568", "#0b4050", "#072b36",
]);

export const cemmColors = {
  blue,
  teal: blue,
  cyan: blue,
  green: scale([
    "#f5f9ec", "#e7f1d2", "#d2e5ac", "#b8d47c", "#a3c955",
    "#96c03a", "#7aa02a", "#5e7d20", "#48601a", "#354716", "#1f2a0c",
  ]),
  orange: scale([
    "#fdf2ee", "#fbe1d6", "#f7c3ae", "#f29d7b", "#ee7a50",
    "#e95e30", "#cc4a1f", "#a83b18", "#842f15", "#632412", "#3b150a",
  ]),
  purple: scale([
    "#fcf0f7", "#f8dcec", "#f0b8d8", "#e58abe", "#d75ca3",
    "#c73188", "#a9276f", "#8a2059", "#6b1946", "#4f1434", "#300b1f",
  ]),
  yellow: scale([
    "#fefce6", "#fdf7bf", "#fbef80", "#f7e640", "#f4e01a",
    "#f1dd00", "#c9b800", "#9c8f00", "#756b00", "#554e00", "#332f00",
  ]),
  gray: scale([
    "#f7f9fa", "#eef2f4", "#dfe5e8", "#c8d1d6", "#94a3ab",
    "#6b7c85", "#4f5f68", "#3b4a53", "#26333a", "#172126", "#0d1418",
  ]),
};

/**
 * Where a palette's roles need another step than Chakra's default for it.
 *
 * Green's solid is one step darker than Chakra's (700, not 600), because CeMM
 * Green at 600 cannot carry white text. Its foreground follows, so green text
 * stays readable on its own subtle background (6.0:1).
 */
export const cemmSemanticColors = {
  green: {
    solid: { value: { _light: "{colors.green.700}", _dark: "{colors.green.600}" } },
    fg: { value: { _light: "{colors.green.800}", _dark: "{colors.green.300}" } },
  },
};
